import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const SaviPage = () => {
  const navigate = useNavigate();
  const { isReady, registerRef, visibleSections } = useScrollAnimation(0.1, 100);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const structuredData = useMemo(() => ({
    "name": "About SAVI - Our Philosophy and Approach",
    "description": "Learn about Savi Investing's value-driven investment philosophy, our belief in long-term wealth creation, and our disciplined approach to the stock market.",
    "url": "https://saviinvesting.in/savi"
  }), []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Who We Are | Savi Investing"
        description="Get to know the back story of Savi Investing - a journey through the recent bullish and bearish phases of India's stock market."
      />
      <StructuredData 
        type="AboutPage"
        data={structuredData}
      />
      <Header />
      
      <main className="flex-grow pt-24">
        {/* THE WHAT Section - Brand Color Background */}
        <section
          id="what"
          ref={registerRef('what')}
          data-section="what"
          className="py-24 bg-[#29367A]"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Text Content - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.what ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-white mb-6">THE WHAT</h2>
                <p className="text-lg text-white leading-relaxed">
                  Savi Investing is born out of the experience of two starkly different phases of the Indian stock market. An overwhelmingly emphatic bull phase, followed by an utterly disorienting bear phase - just how capital markets typically behave. And like everything else in life, the market also brings a more profound wealth of insight and wisdom when it goes down, than when making new highs. All of a sudden out of nowhere, it shifts the entire narrative from Price, to Value.
                </p>
              </div>

              {/* Image - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.what ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1649003515353-c58a239cf662"
                    alt="Stock Market Phases"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* THE WHY Section - White Background */}
        <section
          id="why"
          ref={registerRef('why')}
          data-section="why"
          className="py-24 bg-white"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Image - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.why ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1579621970588-a35d0e7ab9b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBhbmFseXNpc3xlbnwwfHx8fDE3NzUxNjMwNTZ8MA&ixlib=rb-4.1.0&q=85"
                    alt="Long-term Growth"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#29367A]/30 to-transparent"></div>
                </div>
              </div>

              {/* Text Content - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.why ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-6">THE WHY</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Having experienced both phases at their extremes, Savi has come to a point where it places a lot more importance on Value, than it does on Price. Because value is something that sustains, unlike price that hardly ever correlates with the asset's underlying worth. In essence, Savi practices and preaches long-term, value-driven investing - something that entails a great deal of character and temperament, than access to information and intellect.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* THE HOW Section - Brand Color Background */}
        <section
          id="how"
          ref={registerRef('how')}
          data-section="how"
          className="py-24 bg-[#29367A]"
        >
          <div className="w-full max-w-7xl mx-auto px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Text Content - Left */}
              <div
                className={`transition-all duration-1000 transform ${
                  visibleSections.how ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                }`}
              >
                <h2 className="text-4xl font-bold text-white mb-6">THE HOW</h2>
                <p className="text-lg text-white leading-relaxed mb-4">
                  Savi, while scouting and examining value, combines qualitative parameters that are hard to measure, with quantitative metrics that are available rather easily or can be worked out. The subjective information together with the objective data then tell a story about that concerned stock, essentially a business - a story that has a reasonably good chance of playing out!
                </p>
                <p className="text-lg text-white leading-relaxed">
                  Savi then invests in that stock, at a price that is either below, or somewhere fairly close, to its ingrained value.
                </p>
              </div>

              {/* Image - Right */}
              <div
                className={`transition-all duration-1000 delay-200 transform ${
                  visibleSections.how ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.pexels.com/photos/6801636/pexels-photo-6801636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Analysis and Research"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 bg-gradient-to-b from-white to-gray-50">
          <div className="w-full max-w-7xl mx-auto px-8 text-center">
            <p className="text-xl text-gray-700 mb-6">
              Discover the select Indian equities that make up our concentrated portfolio.
            </p>
            <Button
              onClick={() => navigate('/portfolio')}
              className="bg-[#29367A] hover:bg-[#1f2859] text-white px-8 py-6 text-base font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              See Our Portfolio
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SaviPage;
